import { ThresholdBar } from '../../../core/components/threshold-bar/ThresholdBar.js';
import type { ThresholdMarker } from '../../../core/components/threshold-bar/types.js';
import { getMarkerPixel } from '../../../core/components/threshold-bar/utils/get-marker-pixel.js';
import { getMarkerValue } from '../../../core/components/threshold-bar/utils/get-marker-value.js';
import { isBoundedRange } from '../../../core/types/type-guards.js';
import { useSetState } from '../../hooks/store/selectors.js';
import { useCategoricalBarChartBaseConfig } from '../../hooks/useCategoricalBarChartBaseConfig.js';
import { useCategoricalBarChartScales } from '../../hooks/useCategoricalBarChartScales.js';
import { useCategoricalBarChartThresholds } from '../../hooks/useCategoricalBarChartThresholds.js';
import { useFormatter } from '../../hooks/useFormatter.js';

interface CategoricalThresholdBarProps {
  length: number;
}

export const CategoricalThresholdBar = ({
  length,
}: CategoricalThresholdBarProps) => {
  const { layout } = useCategoricalBarChartBaseConfig();
  const { valueScale } = useCategoricalBarChartScales();
  const setState = useSetState();
  const formatter = useFormatter();
  const thresholds = useCategoricalBarChartThresholds();

  const handleHover = (id: string | undefined) => {
    setState((prev) => ({
      ...prev,
      hoveredThresholdId: id,
    }));
  };

  const getMarkerProps = (yFirst: number, yLast: number) => ({
    y0: getMarkerPixel(yFirst, valueScale, layout),
    y1: getMarkerPixel(yLast, valueScale, layout),
    value: getMarkerValue({ y0: yFirst, y1: yLast }, formatter),
  });

  const markers: ThresholdMarker[] = thresholds.map((threshold) => {
    const domainMin = isBoundedRange(threshold.data)
      ? threshold.data.value
      : (threshold.data.min as number | null) ?? -Infinity;
    const domainMax = isBoundedRange(threshold.data)
      ? threshold.data.value
      : (threshold.data.max as number | null) ?? Infinity;

    return {
      ...threshold,
      ...getMarkerProps(domainMin, domainMax),
    };
  });

  return markers.length ? (
    <ThresholdBar
      markers={markers}
      length={length}
      layout={layout}
      onHover={handleHover}
    />
  ) : null;
};

CategoricalThresholdBar['displayName'] = 'TimeseriesThresholdBar';
